import { Component, EventEmitter, inject, OnInit, Output } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';
import * as moment from 'moment';
import { NZ_MODAL_DATA, NzModalRef } from 'ng-zorro-antd/modal';
import { NzUploadFile } from 'ng-zorro-antd/upload';
import { Subject } from 'rxjs';
import { AlertService } from 'src/app/_services/alert.service';
import { RequestService } from 'src/app/_services/request.service';
import { BaseComponent } from '../../../../base/base.component';

@Component({
  selector: 'app-second-step-sickness',
  templateUrl: './second-step-sickness.component.html',
  styleUrls: [
    './second-step-sickness.component.scss',
    '../../SicknessRequest/SicknessRequest.component.css',
    '../../RequestModal/RequestPermission.component.css',
    '../../../review-modal/review-modal.component.css'
  ]
})
export class SecondStepSicknessComponent
  extends BaseComponent
  implements OnInit
{
  private readonly destroy$: Subject<void> = new Subject<void>();
  @Output() saved = new EventEmitter<any>();

  public formBuilder: FormBuilder = inject(FormBuilder);
  private readonly requestService = inject(RequestService);
  private readonly alertService = inject(AlertService);
  secondStepForm: FormGroup;
  // input arrivati dal componente Calendar
  readonly modalData: any = inject(NZ_MODAL_DATA);

  request?: any;
  user: any;
  date: string;
  edit: boolean;
  fileList: NzUploadFile[] = [];
  canCreatePastRequest = false;
  private readonly partTimeNonWorkingDateCache = new Map<string, boolean>();
  private readonly loadedPartTimeWeeks = new Set<string>();
  private readonly loadingPartTimeWeeks = new Set<string>();
  constructor(private readonly modalRef: NzModalRef) {
    super();
  }

  ngOnInit() {
    this.request = this.modalData.request;
    this.user = this.modalData.user ?? this.modalData.request;
    this.date = this.modalData.date;
    this.edit = this.modalData.edit;
    this.canCreatePastRequest = !!this.modalData.canCreatePastRequest;

    this.seedPartTimeWeekFromCalendarPayload();
    this._initForm();
    if (this.edit) {
      this.secondStepForm = this.formBuilder.group({
        start_date: this.request.start_date,
        end_date: this.request.end_date,
        code: this.request.protocolNumber ?? '',
        uploadFile: this.request.uploadFile ?? '',
        note: this.request.notes ?? null,
        event_type_id: 6
      });
    }
  }

  OnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Inizializzo il form
   */
  _initForm() {
    this.secondStepForm = this.formBuilder.group({
      event_id: new FormControl(null),
      start_date: this.date,
      end_date: new FormControl(null, Validators.required),
      code: new FormControl(null, [
        Validators.required,
        Validators.minLength(5),
        Validators.maxLength(13)
      ]),
      certificate: new FormControl(null),
      note: new FormControl(null),
      event_type_id: 6
    });
  }

  /**
   * Questa funzione serve per permettere l'inserimento solo di numeri nell'input del codice di protocollo
   * @param event evento della tastiera
   */
  onlyNumbers(event: KeyboardEvent) {
    const charCode = event.which ? event.which : event.keyCode;
    // Blocca se non è un numero (48–57 sono i codici ASCII delle cifre 0-9)
    if (charCode < 48 || charCode > 57) {
      event.preventDefault();
    }
  }

  /**
   * Funzione generica che gestisce l'upload per tutti i file
   * @param file upload del file
   * @param type il file può essere di tipo cover o logo
   * @returns Salva il file nella lista
   */
  beforeUpload = (file: NzUploadFile): boolean => {
    this.fileList = [file];
    this.handleUpload(file);
    return false;
  };

  removeFile = (): boolean => {
    // La freccia '=>' garantisce che 'this' sia sempre l'istanza del componente

    // 1. Resetta il form control
    this.secondStepForm.patchValue({
      certificate: null
    });

    // 2. Resetta la lista visiva (non strettamente necessario ma pulito)
    this.fileList = [];

    // 3. Permette la rimozione visiva
    return true;
  };

  /**
   * prendo il file che ho caricato e lo manipolo per come mi serve
   */
  handleUpload(file: NzUploadFile): void {
    const reader = new FileReader();
    reader.onload = () => {
      const base64String = reader.result as string;

      // separa header e contenuto
      const arr = base64String.split(',');
      const mime = arr[0].match(/:(.*?);/)![1];
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);

      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }

      // crea Blob -> File
      const blob = new Blob([u8arr], { type: mime });
      const fileFromBlob = new File([blob], file.name, { type: mime });

      // aggiorna il form con un file vero e proprio
      this.secondStepForm.patchValue({
        certificate: fileFromBlob
      });
    };

    reader.readAsDataURL(file as any);
  }

  /**
   * Questa funzione serve per salvare la richiesta di malattia inviando tutti i dati al backend
   * prendendo i dati dal form e aggiungendo l'event_id che arriva dalla request
   */
  saveSickRequest() {
    this.secondStepForm.patchValue({
      event_id: this.request.id,
      end_date: moment(this.secondStepForm.value.end_date).format('YYYY-MM-DD'),
      code: String(this.secondStepForm.value.code)
    });
    const formData = new FormData();
    if (this.secondStepForm.get('certificate')?.value)
      formData.append(
        'certificate',
        this.secondStepForm.get('certificate')?.value
      );

    formData.append('code', this.secondStepForm.get('code')?.value);
    formData.append('start_date', this.secondStepForm.get('start_date')?.value);
    formData.append('end_date', this.secondStepForm.get('end_date')?.value);
    formData.append('event_id', this.secondStepForm.get('event_id')?.value);
    formData.append(
      'event_type_id',
      this.secondStepForm.get('event_type_id')?.value
    );
    const note = this.secondStepForm.get('note')?.value;
    if (note) {
      formData.append('note', note);
    }

    // vanno codificate le date stratDate and end_date prima di mandare la richiesta
    this.requestService.confirmIllnessRequest(formData).subscribe({
      next: () => {
        const text = 'di malattia';
        this.alertService.showSuccessRequestAlert(text, this.date);
        this.saved.emit({ success: true, date: new Date(this.date) });
        this.modalRef.close();
      },
      error: () => {
        this.alertService.showErrorRequestAlert();
      }
    });
  }

  /**
   * chiudi modale
   */
  closeModal() {
    this.modalRef.close();
  }

  /**
   * limita il testo della descrizione non oltre i 150 caratteri
   */
  limitTextLength(): void {
    const control = this.secondStepForm.get('note');
    if (control) {
      const text = control.value || '';
      if (text.length > 150) {
        control.setValue(text.substring(0, 250), { emitEvent: false });
      }
    }
  }

  override disablePastAndWeekendDates = (current: Date): boolean => {
    if (this.isPartTimeNonWorkingDate(current)) {
      return true;
    }

    if (this.canCreatePastRequest) {
      return this.utilityService.disabledDate(current);
    }

    const today = moment();
    const date = moment(current);

    return (
      date.isBefore(today, 'day') || this.utilityService.disabledDate(current)
    );
  };

  disableEndDate = (current: Date): boolean => {
    const raw = this.secondStepForm?.get('start_date')?.value;
    const startDate = raw ? moment(raw) : null;
    const currentDate = moment(current);

    if (startDate?.isValid() && currentDate.isBefore(startDate, 'day')) {
      return true;
    }

    if (startDate?.isValid() && currentDate.isSame(startDate, 'day')) {
      return false; // la startDate è selezionabile come end date
    }

    return this.disablePastAndWeekendDates(current);
  };

  /**
   * Usa la settimana già caricata nel calendario per bloccare subito i giorni
   * non lavorativi part-time nel datepicker del certificato.
   */
  private seedPartTimeWeekFromCalendarPayload(): void {
    const calendarDays = this.modalData?.user?.days ?? [];

    calendarDays.forEach((day: any) => {
      if (!day?.date || !day?.part_time_schedule?.is_part_time) {
        return;
      }

      this.partTimeNonWorkingDateCache.set(
        day.date,
        !day.part_time_schedule.is_working_day
      );
      this.loadedPartTimeWeeks.add(
        moment(day.date).startOf('isoWeek').format('YYYY-MM-DD')
      );
    });
  }

  /**
   * Carica al bisogno la distribuzione effettiva part-time della settimana
   * richiesta quando il datepicker naviga fuori dalla settimana corrente.
   */
  private ensurePartTimeWeekLoaded(date: Date | null): void {
    const userId = this.modalData?.user?.id ?? this.request?.user_id;

    if (!date || !userId) {
      return;
    }

    const weekStart = moment(date).startOf('isoWeek').format('YYYY-MM-DD');

    if (
      this.loadedPartTimeWeeks.has(weekStart) ||
      this.loadingPartTimeWeeks.has(weekStart)
    ) {
      return;
    }

    this.loadingPartTimeWeeks.add(weekStart);

    this.requestService.getPartTimeWeek(userId, weekStart).subscribe({
      next: (response: any) => {
        const result = response?.result;
        const days = result?.days ?? [];
        const isPartTime = !!result?.contract?.is_part_time;

        if (isPartTime) {
          days.forEach((day: any) => {
            const dateKey = moment(weekStart)
              .add((day.weekday ?? 1) - 1, 'days')
              .format('YYYY-MM-DD');

            this.partTimeNonWorkingDateCache.set(dateKey, !day.is_working_day);
          });
        }

        this.loadedPartTimeWeeks.add(weekStart);
        this.loadingPartTimeWeeks.delete(weekStart);
      },
      error: () => {
        this.loadingPartTimeWeeks.delete(weekStart);
      }
    });
  }

  /**
   * Dice se la data selezionata è non lavorativa per il part-time effettivo
   * dell'utente proprietario della malattia.
   */
  private isPartTimeNonWorkingDate(date: Date | null): boolean {
    if (!date) {
      return false;
    }

    const dateKey = moment(date).format('YYYY-MM-DD');
    this.ensurePartTimeWeekLoaded(date);

    return this.partTimeNonWorkingDateCache.get(dateKey) === true;
  }
}
